import { useApolloClient } from '@apollo/client';
import { useCallback } from 'react';

import { UploadedDocumentTypeGQLType } from '../../../../../types/dashboard-core-gqlTypes.generated.d';
import { GET_DOCUMENT_UPLOAD_SIGNED_URL } from '~/packages/credit-facility/requests/requests.gql';
import { GetUploadedDocumentsDocumentGQLType } from '~/packages/credit-facility/requests/requests.gql.generated';
import { presignedPostUpload } from '~/packages/files/ui/utils';

// The helper throws and captures a sentry exception if an error occurs
type DirectUpload = ({
  file,
  creditFacilityId,
  capitalProviderId,
  uploadedDocumentType,
}: {
  file: File;
  creditFacilityId: string;
  capitalProviderId: string | null;
  uploadedDocumentType: UploadedDocumentTypeGQLType;
}) => Promise<{
  s3Bucket: string;
  s3Path: string;
  creditFacilityDocumentId: string;
}>;

export const useDocumentUploadDirectUpload = (): {
  directUpload: DirectUpload;
} => {
  const apolloClient = useApolloClient();

  // The helper throws and captures a sentry exception if an error occurs
  const directUpload = useCallback(
    async ({
      file,
      creditFacilityId,
      capitalProviderId,
      uploadedDocumentType,
    }: {
      file: File;
      creditFacilityId: string;
      capitalProviderId: string | null;
      uploadedDocumentType: UploadedDocumentTypeGQLType;
    }) => {
      const { queryResponse } = await presignedPostUpload<
        {
          documentName: string;
          creditFacilityId: string;
          capitalProviderId: string | null;
          documentType: UploadedDocumentTypeGQLType;
        },
        { s3Bucket: string; s3Path: string; creditFacilityDocumentId: string }
      >({
        client: apolloClient,
        file,
        query: GET_DOCUMENT_UPLOAD_SIGNED_URL,
        queryVariables: {
          documentName: file.name,
          creditFacilityId,
          capitalProviderId:
            uploadedDocumentType === 'participationAgreement'
              ? capitalProviderId
              : null,
          documentType: uploadedDocumentType,
        },
      });

      await apolloClient.refetchQueries({
        include: [GetUploadedDocumentsDocumentGQLType],
      });

      return queryResponse;
    },
    [apolloClient],
  );
  return { directUpload };
};
